import { JobState, JobStateEvent } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { cloneDeep } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';

import {
  BulkActionsBar,
  BulkActionsBarCountButton,
} from '~components/BulkActionsBar/BulkActionsBar';
import { AccountCapacityTypeahead } from '~hooks/useAccount/models';
import { Job } from '~hooks/useJob/models';
import { Order } from '~hooks/useOrders/models';
import { Nullable } from '~types/Nullable';
import { isJobAssignable, isJobCopyable, isJobSendable } from '~utils/jobs/job-utils';
import { getOrderCheckedState } from '~utils/orders';

enum BulkActionsDialog {
  ASSIGN = 'assign',
  CANCEL = 'cancel',
  COPY_VENDOR_ASSIGNMENTS = 'copyVendorAssignments',
  EDIT_JOB_NOTES = 'editJobNotes',
  TEXT_ALL_DRIVERS = 'textAllDrivers',
}

type CheckedOrdersAndJobState = Record<string, string[]>;

type DialogsState = {
  [BulkActionsDialog.ASSIGN]: { isOpen: boolean; selectedVendorAccountId?: string };
  [BulkActionsDialog.CANCEL]: { isOpen: boolean };
  [BulkActionsDialog.COPY_VENDOR_ASSIGNMENTS]: {
    isOpen: boolean;
    selectedOrderId?: string;
  };
  [BulkActionsDialog.EDIT_JOB_NOTES]: { isOpen: boolean };
  [BulkActionsDialog.TEXT_ALL_DRIVERS]: { isOpen: boolean };
};

export function useOrdersViewBulkActionsBar(orderJobs: ReadonlyMap<string, Job[]>) {
  const [checkedOrdersAndJobs, setCheckedOrdersAndJobs] =
    useState<CheckedOrdersAndJobState>({});
  const [dialogsState, setDialogsState] = useState<DialogsState>({
    [BulkActionsDialog.ASSIGN]: { isOpen: false },
    [BulkActionsDialog.CANCEL]: { isOpen: false },
    [BulkActionsDialog.COPY_VENDOR_ASSIGNMENTS]: { isOpen: false },
    [BulkActionsDialog.EDIT_JOB_NOTES]: { isOpen: false },
    [BulkActionsDialog.TEXT_ALL_DRIVERS]: { isOpen: false },
  });

  const checkedJobIds = useMemo(() => {
    return Object.values(checkedOrdersAndJobs).flat();
  }, [checkedOrdersAndJobs]);
  const jobsCount = checkedJobIds.length;

  const sendableJobs = useMemo(() => {
    const selectedJobs = Object.values(checkedOrdersAndJobs).flat();
    const sendableJobIds: string[] = [];

    for (const [_orderId, jobsInOrder] of orderJobs) {
      jobsInOrder.forEach((job) => {
        const isSelected = selectedJobs.includes(job.id);

        if (isSelected) {
          const isSendable = isJobSendable(job);

          if (isSendable) {
            sendableJobIds.push(job.id);
          }
        }
      });
    }

    return sendableJobIds;
  }, [checkedOrdersAndJobs, orderJobs]);

  const assignableJobs = useMemo(() => {
    const selectedJobs = Object.values(checkedOrdersAndJobs).flat();
    const assignableJobIds: string[] = [];

    for (const [_orderId, jobsInOrder] of orderJobs) {
      jobsInOrder.forEach((job) => {
        const isSelected = selectedJobs.includes(job.id);

        if (isSelected) {
          const isAssignable = isJobAssignable(job);

          if (isAssignable) {
            assignableJobIds.push(job.id);
          }
        }
      });
    }

    return assignableJobIds;
  }, [checkedOrdersAndJobs, orderJobs]);

  const unassignableJobs = useMemo(() => {
    const selectedJobs = Object.values(checkedOrdersAndJobs).flat();
    const unassignableJobIds: string[] = [];

    for (const [_orderId, jobsInOrder] of orderJobs) {
      jobsInOrder.forEach((job) => {
        const isSelected = selectedJobs.includes(job.id);

        if (isSelected) {
          const isUnassignable = job.stateEvents.includes(JobStateEvent.UNASSIGN);

          if (isUnassignable) {
            unassignableJobIds.push(job.id);
          }
        }
      });
    }

    return unassignableJobIds;
  }, [checkedOrdersAndJobs, orderJobs]);

  const cancelableJobs = useMemo(() => {
    const selectedJobs = Object.values(checkedOrdersAndJobs).flat();
    const cancelableJobIds: string[] = [];

    for (const [_orderId, jobsInOrder] of orderJobs) {
      jobsInOrder.forEach((job) => {
        const isSelected = selectedJobs.includes(job.id);

        if (isSelected) {
          const isCancelable = job.stateEvents.includes(JobStateEvent.CANCEL);

          if (isCancelable) {
            cancelableJobIds.push(job.id);
          }
        }
      });
    }

    return cancelableJobIds;
  }, [checkedOrdersAndJobs, orderJobs]);

  const copyableJobs = useMemo(() => {
    const selectedJobs = Object.values(checkedOrdersAndJobs).flat();
    const copyableJobIds: string[] = [];

    for (const [_orderId, jobsInOrder] of orderJobs) {
      jobsInOrder.forEach((job) => {
        const isSelected = selectedJobs.includes(job.id);

        if (isSelected && isJobCopyable(job)) {
          copyableJobIds.push(job.id);
        }
      });
    }

    return copyableJobIds;
  }, [checkedOrdersAndJobs, orderJobs]);

  const jobsWithTextableDrivers = useMemo(() => {
    const selectedJobs = Object.values(checkedOrdersAndJobs).flat();
    const jobIds: string[] = [];

    for (const [_orderId, jobsInOrder] of orderJobs) {
      jobsInOrder.forEach((job) => {
        const isSelected = selectedJobs.includes(job.id);

        if (isSelected) {
          const hasDriver = Boolean(job.driver);
          const isInactiveJob = [
            JobState.CANCELED,
            JobState.COMPLETED,
            JobState.CREATED,
            JobState.REJECTED,
          ].includes(job.status);
          const canTextDriver = hasDriver && !isInactiveJob;

          if (canTextDriver) {
            jobIds.push(job.id);
          }
        }
      });
    }

    return jobIds;
  }, [checkedOrdersAndJobs, orderJobs]);

  const jobsWithEditableNotes = useMemo(() => {
    const selectedJobs = Object.values(checkedOrdersAndJobs).flat();
    const jobIds: string[] = [];
    const uniqueOrdersHavingCheckedJobs = new Set<string>();
    let defaultNotes: Nullable<string> = null;

    for (const [orderId, jobsInOrder] of orderJobs) {
      jobsInOrder.forEach((job) => {
        const isSelected = selectedJobs.includes(job.id);

        if (isSelected) {
          jobIds.push(job.id);
          uniqueOrdersHavingCheckedJobs.add(orderId);

          // We only want to set a value default notes, if all the selected jobs have the same notes
          if (defaultNotes === null) {
            defaultNotes = job.notes;
          } else if (defaultNotes !== job.notes) {
            defaultNotes = '';
          }
        }
      });
    }

    return { defaultNotes, jobIds, uniqueOrdersHavingCheckedJobs };
  }, [checkedOrdersAndJobs, orderJobs]);

  const clearAllCheckedOrdersAndJobs = useCallback(() => {
    setCheckedOrdersAndJobs({});
  }, []);

  const updateCheckedOrdersAndJobs = useCallback(
    (orderId: string, jobId?: string) => {
      const jobsInOrder = orderJobs.get(orderId);

      if (!jobsInOrder) {
        return;
      }

      setCheckedOrdersAndJobs((state) => {
        const newCheckedOrdersAndJobs = cloneDeep(state);
        const orderHasCheckedJobs =
          getOrderCheckedState(orderJobs, orderId, state[orderId]) !== false;

        // User is checking a single job
        if (jobId) {
          const jobIdsInOrder = jobsInOrder.map((job) => job.id);
          const checkedJobIdsInOrder = (state[orderId] ?? []).filter((id) =>
            jobIdsInOrder.includes(id),
          );
          const nextCheckedJobIds = checkedJobIdsInOrder.includes(jobId)
            ? checkedJobIdsInOrder.filter((id) => id !== jobId)
            : [...checkedJobIdsInOrder, jobId];

          if (nextCheckedJobIds.length > 0) {
            newCheckedOrdersAndJobs[orderId] = nextCheckedJobIds;
          } else {
            delete newCheckedOrdersAndJobs[orderId];
          }
        }

        // User is checking an order that has checked jobs -> uncheck the order
        else if (orderHasCheckedJobs) {
          delete newCheckedOrdersAndJobs[orderId];
        }

        // User is checking an order that is not already chcked -> check the order and all the
        // Jobs within the order
        else if (jobsInOrder.length > 0) {
          newCheckedOrdersAndJobs[orderId] = jobsInOrder.map((job) => job.id);
        }

        return newCheckedOrdersAndJobs;
      });
    },
    [orderJobs],
  );

  const updateBulkAssignJobsSelectedAccount = useCallback(
    (account?: AccountCapacityTypeahead) => {
      setDialogsState((state) => ({
        ...state,
        [BulkActionsDialog.ASSIGN]: {
          isOpen: true,
          selectedVendorAccountId:
            state[BulkActionsDialog.ASSIGN].selectedVendorAccountId === account?.id
              ? undefined
              : account?.id,
        },
      }));
    },
    [],
  );

  const updateBulkCopyAssignmentsSelectedOrder = useCallback((order: Order) => {
    setDialogsState((state) => ({
      ...state,
      [BulkActionsDialog.COPY_VENDOR_ASSIGNMENTS]: {
        isOpen: true,
        selectedOrderId: order.id,
      },
    }));
  }, []);

  const openDialog = useCallback((dialog: `${BulkActionsDialog}`) => {
    setDialogsState((state) => ({
      ...state,
      [dialog]: { isOpen: true },
    }));
  }, []);

  const closeDialog = useCallback((dialog: `${BulkActionsDialog}`) => {
    setDialogsState((state) => ({
      ...state,
      [dialog]: { isOpen: false },
    }));
  }, []);

  return {
    assignableJobs,
    cancelableJobs,
    unassignableJobs,
    checkedJobIds,
    checkedOrdersAndJobs,
    clearAllCheckedOrdersAndJobs,
    closeDialog,
    copyableJobs,
    dialogsState,
    jobsCount,
    jobsWithEditableNotes,
    jobsWithTextableDrivers,
    openDialog,
    sendableJobs,
    updateBulkAssignJobsSelectedAccount,
    updateBulkCopyAssignmentsSelectedOrder,
    updateCheckedOrdersAndJobs,
  };
}

interface OrdersViewBulkActionsBarProps {
  jobsCount: number;
  onClearAll: () => void;
}

export default function OrdersViewBulkActionsBar({
  children,
  jobsCount,
  onClearAll,
}: React.PropsWithChildren<OrdersViewBulkActionsBarProps>) {
  return (
    <BulkActionsBar>
      <BulkActionsBarCountButton onClick={onClearAll}>
        {t('dispatch.dispatch_v2.count_jobs_selected', { count: jobsCount })}
      </BulkActionsBarCountButton>

      {children}
    </BulkActionsBar>
  );
}
